// Role-gated route wrapper. Unauthenticated users go to /login; authenticated
// users without an allowed role are bounced to their own home.
//
// Session ASB: when the SERVER ended the session (idle, expired, signed out elsewhere), the sign-in page is told why
// with ?reason=, so it can say "Signed out after inactivity" instead of showing a bare sign-in form.
import type { ReactNode } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { homeFor } from "../lib/nav";
import type { Role } from "../lib/api";
import { FullScreenLoader } from "./Loader";

export function ProtectedRoute({
  allow,
  children,
  loginPath = "/login",
}: {
  allow: Role[];
  children: ReactNode;
  // Which sign-in screen to bounce an unauthenticated user to: office routes use the
  // Google sign-in (/login); engineer routes use /engineer/login.
  loginPath?: string;
}) {
  const { user, loading, sessionEndReason } = useAuth();
  const location = useLocation();

  if (loading) return <FullScreenLoader />;
  // Session PN1: the page asked for is remembered in the navigation state (never the URL), so an engineer who taps a notification while
  // signed out lands on that page, not the home screen, once they have entered their code. Only an in-app path is ever kept.
  if (!user) {
    return <Navigate to={sessionEndReason ? `${loginPath}?reason=${sessionEndReason}` : loginPath} replace state={{ from: location.pathname }} />;
  }
  if (!allow.includes(user.role)) return <Navigate to={homeFor(user.role)} replace />;

  return <>{children}</>;
}
